import { z } from 'zod';

/**
 * Common, reusable Zod fragments. Compose into larger schemas rather than
 * re-declaring these inline.
 *
 * Zod 4 notes:
 * - `z.uuid()`, `z.email()`, etc. are now top-level helpers (the
 *   `z.string().uuid()` chain still works but is deprecated).
 * - `z.record(K, V)` requires both key and value schemas (the single-arg form
 *   was removed).
 * - `ZodError.errors` was renamed to `ZodError.issues` (handled in
 *   AllExceptionsFilter).
 */

export const UuidSchema = z.uuid({ message: 'Invalid UUID' });

export const EmailSchema = z.email({ message: 'Invalid email address' }).trim().toLowerCase();

/**
 * Email as it already exists in the database - NOT re-validated.
 *
 * Use this in *response* schemas; use `EmailSchema` in *input* schemas.
 *
 * Rationale: years of imported/legacy rows hold values that `z.email()`
 * rejects ("n/a", "abc@gmail", trailing junk). Validating those on the way
 * OUT makes a single bad row poison a whole page - e.g. the leads Excel
 * export blew up on `data[74].customer.email` and no rows exported at all.
 * Reads must surface what is stored; only writes get to reject it.
 */
export const StoredEmailSchema = z.string().trim().toLowerCase();

export const IsoDateTimeSchema = z.iso.datetime({
  offset: true,
  message: 'Invalid ISO 8601 timestamp',
});

export const CurrencyCodeSchema = z
  .string()
  .length(3, 'Currency must be ISO 4217 (3 letters)')
  .regex(/^[A-Z]{3}$/, 'Currency must be uppercase ISO 4217 (e.g. INR, USD)');

export const MoneyAmountSchema = z
  .number()
  .or(
    z.string().regex(/^-?\d+(\.\d{1,4})?$/, 'Amount must be a number with up to 4 decimal places'),
  )
  .transform((v) => (typeof v === 'string' ? Number(v) : v));

export const MoneySchema = z.object({
  amount: MoneyAmountSchema,
  currency: CurrencyCodeSchema,
});

/**
 * Indian rupees stored as paise (1 INR = 100 paise). Accepts non-negative
 * integers up to JS-safe-int (covers ~₹90 trillion - well past any realistic
 * booking value). The DB column is BigInt; on the wire we use a plain number
 * so clients don't need BigInt parsing pain.
 */
export const PaiseSchema = z
  .number()
  .int('Paise must be an integer')
  .nonnegative('Paise must be non-negative')
  .max(Number.MAX_SAFE_INTEGER, 'Paise exceeds JS safe integer');

export const SortDirectionSchema = z.enum(['asc', 'desc']);

/**
 * String-aware boolean for query params. `z.coerce.boolean()` uses JS
 * `Boolean(...)` which makes the string `"false"` truthy - so a query
 * like `?isInternational=false` ends up TRUE on the server, the opposite
 * of what the operator typed. This helper normalises the common
 * string forms before the boolean coercion:
 *
 *   - "true"  / "1" / "yes" -> true
 *   - "false" / "0" / "no" / ""  -> false
 *   - boolean values pass through verbatim
 *
 * Anything else falls through to the standard zod boolean parser so
 * `null` / unknown shapes still fail validation visibly. Use this in
 * any new list-query schema that accepts a boolean filter.
 */
export const BooleanQueryParamSchema = z.preprocess((v) => {
  if (typeof v === 'string') {
    const norm = v.trim().toLowerCase();
    if (norm === 'true' || norm === '1' || norm === 'yes') return true;
    if (norm === 'false' || norm === '0' || norm === 'no' || norm === '') return false;
  }
  return v;
}, z.boolean());

export const ApiErrorSchema = z.object({
  statusCode: z.number().int(),
  message: z.string(),
  code: z.string().optional(),
  details: z.record(z.string(), z.unknown()).optional(),
  timestamp: IsoDateTimeSchema.optional(),
  path: z.string().optional(),
  traceId: z.string().optional(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;
export type Money = z.infer<typeof MoneySchema>;
